import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import {
  batchPaymentStatus,
  confirmPayment,
  getBatch,
  listPayments,
  recordPayment,
  rejectPayment,
  type Batch,
  type Payment,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { DataTable, type Column } from "@/components/DataTable";
import { PagedFooter } from "@/components/Pagination";
import { useBatchLabel } from "@/components/batchLabel";
import { BatchPicker } from "@/components/BatchPicker";
import { formatDate, titleStatus } from "@/components/format";
import { EmptyState, ErrorBanner, StatusBadge, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { MemberPicker, type PickedMember } from "@/features/payments/MemberPicker";
import { formatAmount, parseMajorAmount } from "@/features/payments/amount";
import { pageParams } from "@/routes/paging";

export const PAYMENT_STATUSES = ["unconfirmed", "confirmed", "rejected", "all"] as const;
export type PaymentStatusFilter = (typeof PAYMENT_STATUSES)[number];
const DEFAULT_STATUS: PaymentStatusFilter = "unconfirmed";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type PaymentPurpose = "induction" | "reissue";
const selectClass = "rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";

const paymentsApi = getRouteApi("/staff/payments");

export function PaymentsWorkspace() {
  const search = paymentsApi.useSearch();
  const status = search.status ?? DEFAULT_STATUS;
  const batch = search.batch;
  const purpose = search.purpose;
  const { page, page_size } = search;
  const batchName = useBatchLabel(batch);
  const { me, can } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [comment, setComment] = useState("");

  const results = useQuery({
    queryKey: ["payments", status, batch ?? "", purpose ?? "", page, page_size],
    queryFn: () =>
      listPayments({
        status: status === "all" ? undefined : status,
        batchId: batch,
        purpose,
        ...pageParams(search),
      }),
  });
  const items = results.data?.items ?? [];

  const settle = async () => {
    setRejecting(null);
    setComment("");
    await queryClient.invalidateQueries({ queryKey: ["payments"] });
    await queryClient.invalidateQueries({ queryKey: ["batch-payments"] });
    await queryClient.invalidateQueries({ queryKey: ["reissue-payments"] });
  };
  const confirm = useMutation({ mutationFn: (id: string) => confirmPayment(id), onSuccess: settle });
  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => rejectPayment(id, note),
    onSuccess: settle,
  });
  const busy = confirm.isPending || reject.isPending;
  const canConfirm = can("payment.confirm");

  // Not selectable, so the decision buttons sit in plain cards rather than inside a button.
  const columns: Column<Payment>[] = [
    {
      key: "batch",
      header: "Batch",
      primary: true,
      className: "align-top",
      cell: (payment) => (
        <>
          <span className="block font-medium text-zinc-950">
            {payment.purpose === "reissue" ? "Re-issue" : (payment.batch_name ?? "Unnamed batch")}
          </span>
        </>
      ),
    },
    {
      key: "covers",
      header: "Covers",
      className: "align-top",
      cell: (payment) =>
        payment.member_id ? (
          <>
            <span className="block text-zinc-700">{payment.member_full_name}</span>
            <span className="block font-mono text-xs text-zinc-500">{payment.membership_no ?? "-"}</span>
          </>
        ) : (
          <span className="text-zinc-700">Whole batch</span>
        ),
    },
    {
      key: "amount",
      header: "Amount",
      className: "align-top",
      cell: (payment) => (
        <span className="font-medium text-zinc-900">{formatAmount(payment.amount_minor, payment.currency)}</span>
      ),
    },
    {
      key: "reference",
      header: "Reference",
      className: "align-top",
      cell: (payment) => (
        <>
          <span className="block font-mono text-xs text-zinc-800">{payment.reference ?? "-"}</span>
          {payment.note ? <span className="mt-1 block text-xs text-zinc-500">{payment.note}</span> : null}
        </>
      ),
    },
    {
      key: "recorded",
      header: "Recorded",
      className: "align-top",
      cell: (payment) => (
        <>
          <span className="block text-zinc-700">{payment.recorded_by_name ?? "-"}</span>
          <span className="block text-xs text-zinc-500">{formatDate(payment.created_at)}</span>
        </>
      ),
    },
    {
      key: "status",
      header: "Status",
      className: "align-top",
      cell: (payment) => (
        <>
          <StatusBadge status={payment.status} />
          {payment.decision_comment ? (
            <span className="mt-1 block text-xs text-zinc-500">{payment.decision_comment}</span>
          ) : null}
        </>
      ),
    },
    ...(canConfirm
      ? [
          {
            key: "decision",
            header: "Decision",
            className: "align-top",
            cell: (payment: Payment) => (
              <Decision
                payment={payment}
                mine={payment.recorded_by === me?.user.id}
                busy={busy}
                onConfirm={() => confirm.mutate(payment.id)}
                onStartReject={() => {
                  setRejecting(payment.id);
                  setComment("");
                }}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Payments"
        detail="Offline induction payments count toward approval once someone other than the recorder confirms them"
      />
      <ErrorBanner error={results.error ?? confirm.error ?? reject.error} />
      {can("payment.record") ? <RecordPaymentForm key={batch ?? ""} initialBatch={batch ?? ""} /> : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium">Recorded payments</span>
            {batch ? (
              <span className="inline-flex items-center gap-2 rounded-md bg-zinc-100 px-2 py-1 text-xs text-zinc-700">
                <span>Batch: {batchName}</span>
                <button
                  type="button"
                  onClick={() =>
                    void navigate({
                      to: "/payments",
                      search: { status: search.status, purpose, page_size },
                    })
                  }
                  className="font-medium underline"
                >
                  Clear batch filter
                </button>
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              aria-label="Purpose filter"
              value={purpose ?? ""}
              onChange={(e) =>
                void navigate({
                  to: "/payments",
                  search: {
                    status: search.status,
                    batch,
                    purpose: (e.target.value || undefined) as PaymentPurpose | undefined,
                    page_size,
                  },
                })
              }
              className={selectClass}
            >
              <option value="">All purposes</option>
              <option value="induction">Induction</option>
              <option value="reissue">Re-issue</option>
            </select>
            <select
              aria-label="Status filter"
              value={status}
              onChange={(e) =>
                void navigate({
                  to: "/payments",
                  search: {
                    status: e.target.value as PaymentStatusFilter,
                    batch,
                    purpose,
                    page_size,
                  },
                })
              }
              className={selectClass}
            >
              {PAYMENT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value === "all" ? "All statuses" : titleStatus(value)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {results.isLoading ? (
          <EmptyState title="Loading payments" detail="Fetching recorded payments." />
        ) : items.length === 0 ? (
          <EmptyState title="No payments in this view" detail="Change the filters, or record a payment above." />
        ) : (
          <div className="p-3 md:p-0">
            <DataTable
              rows={items}
              columns={columns}
              rowKey={(payment) => payment.id}
              label="Recorded payments"
              tableClassName="min-w-[860px]"
              expanded={(payment) =>
                rejecting === payment.id ? (
                  <RejectForm
                    busy={busy}
                    comment={comment}
                    onComment={setComment}
                    onCancel={() => setRejecting(null)}
                    onReject={() => reject.mutate({ id: payment.id, note: comment.trim() })}
                  />
                ) : null
              }
            />
          </div>
        )}
        <PagedFooter data={results.data} page={page} pageSize={page_size} />
      </div>
    </section>
  );
}

const touch = "min-h-10 md:min-h-0";

function Decision({
  payment,
  mine,
  busy,
  onConfirm,
  onStartReject,
}: {
  payment: Payment;
  mine: boolean;
  busy: boolean;
  onConfirm: () => void;
  onStartReject: () => void;
}) {
  if (payment.status !== "unconfirmed") {
    return <span className="text-xs text-zinc-500">{payment.confirmed_by_name ?? payment.rejected_by_name ?? "-"}</span>;
  }
  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <ToolbarButton tone="primary" className={touch} disabled={busy || mine} onClick={onConfirm}>
          Confirm
        </ToolbarButton>
        <ToolbarButton className={touch} tone="danger" disabled={busy || mine} onClick={onStartReject}>
          Reject
        </ToolbarButton>
      </div>
      {mine ? <p className="text-xs text-amber-700">You recorded this; someone else must confirm it.</p> : null}
    </div>
  );
}

function RejectForm({
  busy,
  comment,
  onComment,
  onCancel,
  onReject,
}: {
  busy: boolean;
  comment: string;
  onComment: (value: string) => void;
  onCancel: () => void;
  onReject: () => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="block w-full flex-1 text-sm md:w-auto md:min-w-72">
        <span className="font-medium text-zinc-700">Rejection comment</span>
        <textarea
          value={comment}
          onChange={(e) => onComment(e.target.value)}
          rows={2}
          maxLength={2000}
          className="mt-1 w-full resize-none rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
      </label>
      <ToolbarButton className={touch} tone="danger" disabled={busy || comment.trim() === ""} onClick={onReject}>
        Reject payment
      </ToolbarButton>
      <ToolbarButton className={touch} disabled={busy} onClick={onCancel}>
        Cancel
      </ToolbarButton>
    </div>
  );
}

function RecordPaymentForm({ initialBatch }: { initialBatch: string }) {
  const queryClient = useQueryClient();
  const [purpose, setPurpose] = useState<PaymentPurpose>("induction");
  const [picked, setPicked] = useState<PickedMember | null>(null);
  const [attempted, setAttempted] = useState(false);
  // Undefined until the user picks, so a batch from the URL shows once it loads.
  const [chosenBatch, setChosenBatch] = useState<Batch | null | undefined>(undefined);
  const initial = useQuery({
    queryKey: ["batch", initialBatch],
    queryFn: () => getBatch(initialBatch),
    enabled: UUID.test(initialBatch),
  });
  const pickedBatch = chosenBatch === undefined ? (initial.data ?? null) : chosenBatch;
  const [memberId, setMemberId] = useState("");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  const trimmedBatch = pickedBatch?.id ?? "";
  const status = useQuery({
    queryKey: ["batch-payments", trimmedBatch],
    queryFn: () => batchPaymentStatus(trimmedBatch),
    enabled: UUID.test(trimmedBatch),
  });
  // Recipients without a member are not numbered yet; only a batch-level payment covers them.
  const members = (status.data?.unpaid ?? []).filter((row) => row.member_id);
  const amountMinor = parseMajorAmount(amount);
  const reissue = purpose === "reissue";
  const ready = (reissue || trimmedBatch !== "") && amountMinor !== null && reference.trim() !== "";

  const record = useMutation({
    mutationFn: () => {
      const rest = {
        amount_minor: amountMinor ?? 0,
        reference: reference.trim(),
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      return recordPayment(
        reissue
          ? { purpose: "reissue", member_id: picked?.id, ...rest }
          : {
              batch_id: trimmedBatch,
              ...(memberId ? { member_id: memberId } : {}),
              ...rest,
            },
      );
    },
    onSuccess: async () => {
      setMemberId("");
      setPicked(null);
      setAttempted(false);
      setAmount("");
      setReference("");
      setNote("");
      await queryClient.invalidateQueries({ queryKey: ["payments"] });
      await queryClient.invalidateQueries({ queryKey: ["batch-payments"] });
      await queryClient.invalidateQueries({ queryKey: ["reissue-payments"] });
    },
  });

  const field =
    "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";
  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">Record a payment</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        <label className="block text-sm xl:col-span-5">
          <span className="font-medium text-zinc-700">Purpose</span>
          <select value={purpose} onChange={(e) => setPurpose(e.target.value as PaymentPurpose)} className={field}>
            <option value="induction">Induction</option>
            <option value="reissue">Re-issue</option>
          </select>
        </label>
        {reissue ? (
          <div className="xl:col-span-5">
            <MemberPicker
              value={picked}
              onChange={setPicked}
              problem={attempted && !picked ? "Choose a member." : undefined}
            />
          </div>
        ) : (
          <>
            <div className="xl:col-span-2">
              <BatchPicker
                value={pickedBatch}
                onChange={(next) => {
                  setChosenBatch(next);
                  setMemberId("");
                }}
              />
            </div>
            <label className="block text-sm xl:col-span-3">
              <span className="font-medium text-zinc-700">Covers</span>
              <select value={memberId} onChange={(e) => setMemberId(e.target.value)} className={field}>
                <option value="">Whole batch</option>
                {members.map((row) => (
                  <option key={row.member_id} value={row.member_id ?? ""}>
                    {row.full_name} ({row.membership_no ?? "no number"}), row {row.row}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Amount (NGN)</span>
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder="25,000.00"
            className={field}
          />
        </label>
        <label className="block text-sm xl:col-span-2">
          <span className="font-medium text-zinc-700">Reference</span>
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Receipt or teller number"
            className={field}
          />
        </label>
        <label className="block text-sm xl:col-span-2">
          <span className="font-medium text-zinc-700">Note</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} className={field} />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <ToolbarButton tone="primary"
          disabled={!ready || record.isPending}
          onClick={() => {
            setAttempted(true);
            if (reissue && !picked) return;
            record.mutate();
          }}
        >
          {record.isPending ? "Recording..." : "Record payment"}
        </ToolbarButton>
        {record.isSuccess ? (
          <p className="text-sm font-medium text-emerald-700">Recorded. It counts once someone else confirms it.</p>
        ) : null}
      </div>
      <ErrorBanner error={record.error} />
    </div>
  );
}
